// web_ui/src/api/client.ts
import { Device, DeviceChartResponse, DeviceChartResponseDto, mapDeviceChartResponse } from "./contracts/deviceTypes"; 
import { UserAuthResponseFromApi } from "./contracts/userTypes";
import { SessionDeviceDto, mapSessionDevice, SessionDevice, RegisterDeviceInput } from "./contracts/deviceTypes";
import { EditDeviceInput, EditDeviceResponse, EditDeviceResponseDto, mapEditDeviceResponse } from "./contracts/deviceTypes"; 

const BASE_URL = "/api/v1";

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API Error: ${res.status} ${res.statusText} - ${text}`);
  }
  return res.json() as Promise<T>;
}

// Utility to map backend snake_case -> frontend camelCase
export function mapDeviceFromBackend(d: any): Device {
  return {
    deviceId: d.device_id,
    deviceName: d.device_name,
    capacityLiters: d.capacity_liters,
    shellyServer: d.shelly_server,
    shellyAuthKey: d.shelly_auth_key,
    shellyDeviceId: d.shelly_device_id,
    shellyChannel: d.shelly_channel,
    dueTimesLocal: d.due_times_local || [],
    relayState: d.relay_state ?? false,
  };
}

// ----------------------
// Login
// ----------------------

export type LoginUserRequest = {
  email: string;
  password: string;
};

export type LoginUserResponse = {
  success: boolean;
  user: {
    id: number;
    name: string;
    region: "EE" | "FI" | "LT" | "LV";
  } | null;
  message?: string;
};

export async function loginUser(
  payload: LoginUserRequest
): Promise<UserAuthResponseFromApi> {
  const res = await fetch(`${BASE_URL}/login_user`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(payload),
  credentials: "include",
});

  if (!res.ok) {
    throw new Error("Login request failed");
  }

  return res.json();
}

// ----------------------
// Register
// ----------------------

export async function registerUser(user: {
  name: string;
  email: string;
  password: string;
  region: string;
}): Promise<UserAuthResponseFromApi> {
  const res = await fetch(`${BASE_URL}/register_user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(user),
  });
  return handleResponse<UserAuthResponseFromApi>(res);
}


// -------------------
// Devices
// -------------------
export async function registerDevice(payload: RegisterDeviceInput): Promise<SessionDevice[]> {
  const res = await fetch(`${BASE_URL}/register_device`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(`Device registration failed: ${res.status}`);
  }

  const data: { success: boolean; devices: SessionDeviceDto[] } = await res.json();

  // Map BE snake_case -> FE camelCase
  return data.devices.map(mapSessionDevice);
}

export async function editDevice(payload: EditDeviceInput): Promise<EditDeviceResponse> {
  const res = await fetch(`${BASE_URL}/edit_device`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(`Edit device failed: ${res.status}`);
  }

  const data: EditDeviceResponseDto = await res.json();

  // Map BE snake_case -> FE camelCase
  return mapEditDeviceResponse(data);
}

export async function fetchUserDevices(): Promise<{ success: boolean; devices: SessionDevice[] }> {
  const res = await fetch(`${BASE_URL}/get_devices`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error("Failed to fetch devices");
  }

  const data: { success: boolean; devices: SessionDeviceDto[] } = await res.json();

  // Map BE snake_case -> FE camelCase
  const devices: SessionDevice[] = data.devices.map(mapSessionDevice);

  return { success: data.success, devices };
}

export async function fetchDeviceChart(
  deviceId: number,
  date: string
): Promise<DeviceChartResponse> {
  const res = await fetch(
    `${BASE_URL}/get_chart_data?device_id=${deviceId}&date=${date}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    }
  );

  if (!res.ok) {
    throw new Error("Failed to fetch device chart data");
  }

  const data: DeviceChartResponseDto = await res.json();

  // Map BE snake_case -> FE camelCase
  return mapDeviceChartResponse(data);
}

export const deleteDevice = async (deviceId: number): Promise<void> => {
  const res = await fetch(`${BASE_URL}/delete_device`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ deviceId }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || "Failed to delete device");
  }
};



export async function logoutUser(): Promise<void> {
  try {
    const res = await fetch(`${BASE_URL}/logout_user`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });

    if (!res.ok) {
      throw new Error(`Logout failed with status ${res.status}`);
    }

    // Optional: log success for debugging
    console.log("Logout successful");

  } catch (err) {
    console.error("Logout error:", err);
    throw err; // re-throw so calling function knows logout failed
  }
}

// -------------------
// Session
// -------------------

export async function checkSession(): Promise<UserAuthResponseFromApi> {
  const res = await fetch(`${BASE_URL}/check_session`, {
    method: "GET",
    credentials: "include", // crucial: send cookies
  });

  if (!res.ok) {
    return { success: false }; // session invalid
  }

  return res.json();
}
